/**
 * 録音音声の簡易品質チェック。ブラウザ側で「録り直し」を促すための事前検証。
 *
 * サーバー側で改めて pyannote が embedding を計算するので、ここでの判定は
 * 「明らかに使えない録音を弾く」レベルにとどめる。
 *
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business voice-enrollment Web の録音品質チェック (rms / 無音率 / 録音長) と閾値判定
 * @graph-connects none
 */

export interface QualityMetrics {
  /** 録音長（秒） */
  durationSec: number;
  /** RMS 音量（0..1） */
  rms: number;
  /** 無音と判定されたフレームの割合（0..1）。閾値以下のサンプルが続いたら無音扱い */
  silenceRatio: number;
}

export interface QualityVerdict {
  metrics: QualityMetrics;
  acceptable: boolean;
  reasons: QualityReason[];
}

export type QualityReason = 'too_short' | 'too_quiet' | 'too_silent';

interface QualityThresholds {
  minDurationSec: number;
  minRms: number;
  maxSilenceRatio: number;
}

/** @graph-connects none */
export const DEFAULT_THRESHOLDS: QualityThresholds = {
  minDurationSec: 4,
  minRms: 0.01,
  maxSilenceRatio: 0.6,
};

/**
 * Float32 の PCM サンプル列から品質メトリクスを計算する。
 *
 * @param samples モノラル PCM サンプル（-1..1）
 * @param sampleRate サンプルレート
 */
/**
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business PCM サンプル列から duration / RMS / 無音率 を計算する純関数
 * @graph-connects none
 */
export function computeQualityMetrics(samples: Float32Array, sampleRate: number): QualityMetrics {
  if (samples.length === 0 || sampleRate <= 0) {
    return { durationSec: 0, rms: 0, silenceRatio: 1 };
  }

  let sumSquares = 0;
  let silentSamples = 0;
  const silenceThreshold = 0.005;
  for (const sample of samples) {
    sumSquares += sample * sample;
    if (Math.abs(sample) < silenceThreshold) {
      silentSamples++;
    }
  }

  const rms = Math.sqrt(sumSquares / samples.length);
  return {
    durationSec: samples.length / sampleRate,
    rms,
    silenceRatio: silentSamples / samples.length,
  };
}

/**
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business 計測値を 3 閾値で判定し、acceptable + 失敗理由 (too_short / too_quiet / too_silent) を返す
 * @graph-connects none
 */
export function evaluateQuality(
  metrics: QualityMetrics,
  thresholds: QualityThresholds = DEFAULT_THRESHOLDS,
): QualityVerdict {
  const reasons: QualityReason[] = [];
  if (metrics.durationSec < thresholds.minDurationSec) {
    reasons.push('too_short');
  }
  if (metrics.rms < thresholds.minRms) {
    reasons.push('too_quiet');
  }
  if (metrics.silenceRatio > thresholds.maxSilenceRatio) {
    reasons.push('too_silent');
  }
  return {
    metrics,
    acceptable: reasons.length === 0,
    reasons,
  };
}
